import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/system';

import getServiceTypes from '@/shared/api/bookings/services/getServiceTypes';
import WaveUIForm_V2 from '@/shared/components/forms/WaveUIForm_V2';
import FieldType from '@/shared/components/forms/WaveUIForm_V2/types/fieldType.enum';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import BookingServiceType from '@/shared/types/bookings/serviceType.type';

import { FormFields } from '..';

type BookingServiceDetailsProps = {
  bookingServiceDetails: FormFields;
  setBookingServiceDetails: (values: FormFields) => void;
};

const BookingServiceDetails: FC<BookingServiceDetailsProps> = ({
  bookingServiceDetails,
  setBookingServiceDetails,
}) => {
  const { t } = useTranslation(['bookingsServices', 'forms', 'common']);
  const redirectLogin = useRedirectLogin();

  const [serviceTypes, setServiceTypes] = useState<BookingServiceType[]>([]);

  useEffect(() => {
    const fetchServiceTypes = async () => {
      const response = await getServiceTypes(() => redirectLogin());

      if (response.success) {
        setServiceTypes(response.data);
      }
    };

    void fetchServiceTypes();
  }, [redirectLogin]);

  return (
    <Box sx={{ py: 1 }}>
      <WaveUIForm_V2
        fields={[
          {
            field_type: FieldType.Select,
            field_props: {
              label: t('forms:booking_type'),
              name: 'booking_type',
              options: [{ label: 'Appointment', value: 'APPOINTMENT' }], // TODO: Replace with actual values
              value: bookingServiceDetails.booking_type.value,
              required: true,
              placeholder: 'Select Booking Type',
              error: !!bookingServiceDetails.booking_type.error,
              helperText: bookingServiceDetails.booking_type.error,
            },
          },
          {
            field_type: FieldType.Select,
            field_props: {
              label: t('forms:booking_service_key'),
              name: 'booking_service_key',
              options: serviceTypes?.map((type) => ({
                label: type.booking_service_type_name,
                value: type.booking_service_key,
              })),
              value: bookingServiceDetails.booking_service_key.value,
              required: true,
              placeholder: 'Select Booking Service Key',
              error: !!bookingServiceDetails.booking_service_key.error,
              helperText: bookingServiceDetails.booking_service_key.error,
            },
          },
          {
            field_type: FieldType.Text,
            field_props: {
              label: t('forms:booking_service_name'),
              name: 'booking_service_name',
              value: bookingServiceDetails.booking_service_name.value,
              required: true,
              placeholder: 'Enter Booking Service Name',
              error: !!bookingServiceDetails.booking_service_name.error,
              helperText: bookingServiceDetails.booking_service_name.error,
            },
          },
        ]}
        getFieldsValues={(value) => {
          const key = Object.keys(value)[0] as keyof FormFields;

          setBookingServiceDetails({
            ...bookingServiceDetails,
            [key]: {
              ...bookingServiceDetails[key],
              value: value[key],
            },
          });
        }}
      />
    </Box>
  );
};

export default BookingServiceDetails;
